import { Database } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { getItemFields, updateFieldQuestion } from '~/api/catalog'
import type { CatalogItem, CatalogItemField } from '~/api/types'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/ui/empty-state'
import { Label } from '~/components/ui/label'
import { Spinner } from '~/components/ui/spinner'
import { useToast } from '~/components/ui/toast'
import { cn } from '~/lib/utils'

// 수집 데이터 · 사전 질의 — 저장된 항목에만 필드를 로드해 표시한다(필드는 항목 id로
// 로드). 새 항목 추가 모드(item === null)에서는 안내만 보여준다.
// 저장은 행별 단건 PUT(updateFieldQuestion). 백엔드가 병행 제공한다.
export function FieldListManager({ item }: { item: CatalogItem | null }) {
    // 수집 데이터 · 사전 질의 — 저장된 항목에만 표시(필드는 항목 id로 로드).
    if (item === null) {
        return (
            <section className='border-border space-y-2 rounded-[var(--radius)] border border-dashed p-4'>
                <h3 className='text-foreground text-sm font-bold'>
                    수집 데이터 · 사전 질의
                </h3>
                <p className='text-muted-foreground text-xs'>
                    항목을 저장한 뒤에 수집 데이터 필드와 사전 질의를 검수할 수
                    있어요.
                </p>
            </section>
        )
    }
    return <FieldsSection itemId={item.id} />
}

function FieldsSection({ itemId }: { itemId: string }) {
    const [fields, setFields] = useState<CatalogItemField[] | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // 드로어가 열릴 때(항목 id) 필드 목록을 로드한다. 언마운트/항목 변경 시 무시.
    useEffect(() => {
        let active = true
        setLoading(true)
        setError(null)
        getItemFields(itemId)
            .then((data) => {
                if (active) setFields(data)
            })
            .catch((e) => {
                if (active) setError((e as Error).message)
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [itemId])

    // 저장 성공 시 부모 목록을 갱신해 커버리지 배지를 최신화한다.
    const handleSaved = (fieldId: string, question: string) => {
        setFields((list) =>
            list
                ? list.map((f) =>
                      f.fieldId === fieldId ? { ...f, question } : f
                  )
                : list
        )
    }

    const total = fields?.length ?? 0
    const answered =
        fields?.filter((f) => (f.question ?? '').trim()).length ?? 0

    return (
        <section className='border-border space-y-4 rounded-[var(--radius)] border p-4'>
            <div className='flex items-start justify-between gap-2'>
                <div>
                    <h3 className='text-foreground text-sm font-bold'>
                        수집 데이터 · 사전 질의
                    </h3>
                    <p className='text-muted-foreground mt-1 text-xs'>
                        평가에 쓰는 수집 데이터와, 사용자에게 물을 사전
                        질의예요. 질의를 검수·수정하면 행별로 저장돼요.
                    </p>
                </div>
                {fields && total > 0 ? (
                    <Badge tone={answered === total ? 'success' : 'warning'}>
                        질의 {answered}/{total}
                    </Badge>
                ) : null}
            </div>

            {loading ? (
                <div className='flex justify-center py-8'>
                    <Spinner label='필드를 불러오는 중' />
                </div>
            ) : error ? (
                <EmptyState
                    icon={Database}
                    title='필드를 불러오지 못했어요.'
                    description={error}
                />
            ) : !fields || fields.length === 0 ? (
                <EmptyState
                    icon={Database}
                    title='수집 데이터 필드가 없어요.'
                    description='이 항목에 연결된 수집 데이터가 아직 없어요.'
                />
            ) : (
                <ul className='space-y-3'>
                    {fields.map((f) => (
                        <FieldRow
                            key={f.fieldId}
                            itemId={itemId}
                            field={f}
                            onSaved={handleSaved}
                        />
                    ))}
                </ul>
            )}
        </section>
    )
}

function FieldRow({
    itemId,
    field,
    onSaved,
}: {
    itemId: string
    field: CatalogItemField
    onSaved: (fieldId: string, question: string) => void
}) {
    const toast = useToast()
    const id = useId()
    const [question, setQuestion] = useState(field.question ?? '')
    const [saving, setSaving] = useState(false)
    const [expanded, setExpanded] = useState(false)

    const dirty = question !== (field.question ?? '')
    const empty = !question.trim()
    const threshold = field.threshold?.trim()

    const save = async () => {
        setSaving(true)
        try {
            await updateFieldQuestion(itemId, field.fieldId, question)
            onSaved(field.fieldId, question)
            toast.success('질의를 수정했어요')
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setSaving(false)
        }
    }

    return (
        <li className='border-border bg-card rounded-[var(--radius)] border p-3'>
            <div className='flex flex-wrap items-center gap-1.5'>
                <span className='text-foreground text-[13px] font-bold'>
                    {field.nameKo}
                </span>
                {field.unit ? <Badge tone='neutral'>{field.unit}</Badge> : null}
                {field.dataType ? (
                    <Badge tone='info'>{field.dataType}</Badge>
                ) : null}
                {empty ? <Badge tone='warning'>질의 없음</Badge> : null}
            </div>

            {threshold ? (
                <button
                    type='button'
                    aria-expanded={expanded}
                    onClick={() => setExpanded((v) => !v)}
                    className={cn(
                        'text-muted-foreground mt-1 block max-w-full text-left text-xs hover:underline',
                        !expanded && 'truncate'
                    )}>
                    기준: {threshold}
                </button>
            ) : null}

            <div className='mt-2'>
                <Label htmlFor={id} className='text-xs'>
                    사전 질의
                </Label>
                <textarea
                    id={id}
                    rows={2}
                    value={question}
                    disabled={saving}
                    placeholder='(질의 없음)'
                    onChange={(e) => setQuestion(e.target.value)}
                    className={cn(
                        'border-input bg-card text-foreground mt-1 w-full rounded-[var(--radius)] border px-3 py-2 text-sm transition-colors',
                        'placeholder:text-muted-foreground',
                        'focus:border-primary focus:ring-ring/30 focus:ring-2 focus:outline-none',
                        empty && 'border-warning/40 bg-warning-subtle/40'
                    )}
                />
                <div className='mt-2 flex justify-end'>
                    <Button
                        size='sm'
                        disabled={!dirty || saving}
                        onClick={save}>
                        {saving ? '저장 중…' : '질의 저장'}
                    </Button>
                </div>
            </div>
        </li>
    )
}
